/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : AircraftDetailsForm.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : AircraftDetails Form component
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Jan 10, 2022
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IOption } from "@scuf/common/dist/components/Select/ISelectProps";
import { Card, Grid, Button, Input } from "@scuf/common";
import {
  selectCurrentApmFileRecord,
  selectCurrentWorkFlowType,
  setCurrentWorkFlowStep,
} from "stores/ApmWorkFlowStore";
import {
  selectAircraftDetailsMasterData,
  setSelectedAircraftDetails,
  selectSelectedAircraftDetails,
  SaveAircraftDetails,
  selectSaveDetailsLoader,
} from "stores/AircraftDetailsStore";
import { useForm } from "react-hook-form";
import { aircraftsDetailsValidationSchema } from "./AircraftsDetailsValidation";
import { IAircraftDetailsFormFields } from "models/aircraftDetails";
import { AircraftDetailsSelect } from "./AircraftDetailsSelect";
import { SELECT_LABEL, SELECT_PLACEHOLDER, HEADING_TEXT } from "./constants";
import { yupResolver } from "@hookform/resolvers/yup";
import { setSettingsResetWarning } from "stores/UIStore";
import {
  EnumApmSettingsWorkFlow,
  EnumNavigateAction,
} from "models/configuration";

export const AircraftDetailsForm: FC = () => {
  const dispatch = useDispatch();
  const saveDetailsLoader = useSelector(selectSaveDetailsLoader);
  const selectedAircraftDetails = useSelector(selectSelectedAircraftDetails);
  const aircraftDetailsMasterData = useSelector(
    selectAircraftDetailsMasterData
  );
  const currentApmFileRecord = useSelector(selectCurrentApmFileRecord);
  const currentWorkFlowType = useSelector(selectCurrentWorkFlowType);
  const [modelOption, setModelOption] = useState<IOption[]>([]);

  const initializeValue: IAircraftDetailsFormFields = {
    make: "",
    model: "",
    tailNumber: "",
    serialNumber: "",
  };
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<IAircraftDetailsFormFields>({
    mode: "all",
    defaultValues: selectedAircraftDetails
      ? selectedAircraftDetails
      : initializeValue,
    resolver: yupResolver(aircraftsDetailsValidationSchema),
  });

  useEffect(() => {
    register("make");
    register("model");
    register("tailNumber");
    register("serialNumber");
  }, [register]);

  const getUpdatedFormValues = watch();
  const makeOptions =
    aircraftDetailsMasterData !== null
      ? Object.keys(aircraftDetailsMasterData).map((make) => {
          return { value: make, text: make };
        })
      : [];

  useEffect(() => {
    if (selectedAircraftDetails && aircraftDetailsMasterData) {
      if (selectedAircraftDetails.make) {
        const ModelOption = aircraftDetailsMasterData[
          selectedAircraftDetails?.make
        ].map((modelItem) => {
          return { value: modelItem, text: modelItem };
        });

        setModelOption(ModelOption);
        if (
          selectedAircraftDetails?.make !== getUpdatedFormValues?.make ||
          selectedAircraftDetails?.model !== getUpdatedFormValues?.model
        ) {
          reset(selectedAircraftDetails);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAircraftDetails, aircraftDetailsMasterData]);

  const changeOnMakeOption = (changedValue: string) => {
    setValue("make", changedValue, { shouldValidate: true });

    const tempModelOption =
      aircraftDetailsMasterData !== null
        ? aircraftDetailsMasterData[changedValue].map((modelItem) => {
            return { value: modelItem, text: modelItem };
          })
        : [{ value: "", text: "" }];
    if (getUpdatedFormValues?.model !== "") {
      setValue("model", "");
    }
    setModelOption(tempModelOption);
  };

  const changeOnModelOption = (changedValue: string) => {
    setValue("model", changedValue, { shouldValidate: true });
  };

  const onClickSaveButton = (
    data: IAircraftDetailsFormFields,
    buttonClicked: string
  ) => {
    dispatch(setSelectedAircraftDetails(data));
    if (
      currentWorkFlowType === EnumApmSettingsWorkFlow.Edit &&
      (currentApmFileRecord?.make !== getValues().make ||
        currentApmFileRecord.model !== getValues().model)
    ) {
      dispatch(
        setSettingsResetWarning({
          showSettingsResetWarningModal: true,
          navigateActionData: data,
          navigateAction:
            buttonClicked === "next"
              ? EnumNavigateAction.next
              : EnumNavigateAction.save,
        })
      );
    } else {
      dispatch(SaveAircraftDetails(data, buttonClicked));
    }
  };

  return (
    <div className="aircraft-details-container">
      <form>
        <Card>
          <Card.Header className="aircraft-details-heading">
            <Grid>
              <Grid.Row>
                <Grid.Column
                  className="header-label-padding"
                  lWidth={3}
                  xsWidth={12}
                >
                  <h3>{HEADING_TEXT.confirmMsg}</h3>
                </Grid.Column>
                <Grid.Column
                  className="header-label-padding header-label-sub-text"
                  lWidth={9}
                  xsWidth={12}
                >
                  (Select or enter following details to add new Aircraft
                  details)
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Card.Header>
          <Card.Content className="fields-box">
            <Grid>
              <Grid.Row>
                <Grid.Column lWidth={3} sWidth={6} mWidth={3} xsWidth={12}>
                  <AircraftDetailsSelect
                    label={SELECT_LABEL.make}
                    placeholder={SELECT_PLACEHOLDER.make}
                    name="make"
                    value={
                      getUpdatedFormValues && getUpdatedFormValues.make
                        ? getUpdatedFormValues?.make
                        : ""
                    }
                    changeOption={changeOnMakeOption}
                    options={makeOptions}
                    error={errors && errors.make ? errors.make.message : ""}
                  />
                </Grid.Column>
                <Grid.Column lWidth={3} sWidth={6} xsWidth={12} mWidth={3}>
                  <AircraftDetailsSelect
                    label={SELECT_LABEL.model}
                    placeholder={SELECT_PLACEHOLDER.model}
                    name="model"
                    value={
                      getUpdatedFormValues && getUpdatedFormValues.model
                        ? getUpdatedFormValues?.model
                        : ""
                    }
                    changeOption={changeOnModelOption}
                    selectedMake={getUpdatedFormValues}
                    options={modelOption}
                    error={errors && errors.model ? errors.model.message : ""}
                  />
                </Grid.Column>
                <Grid.Column lWidth={3} sWidth={6} xsWidth={12} mWidth={3}>
                  <Input
                    label={SELECT_LABEL.tail}
                    indicator="required"
                    name="tailNumber"
                    placeholder={SELECT_PLACEHOLDER.tail}
                    value={
                      getUpdatedFormValues && getUpdatedFormValues.tailNumber
                        ? getUpdatedFormValues?.tailNumber
                        : ""
                    }
                    onChange={(data) => {
                      setValue("tailNumber", data, { shouldValidate: true });
                    }}
                    error={
                      errors && errors.tailNumber
                        ? errors.tailNumber.message
                        : ""
                    }
                  />
                </Grid.Column>
                <Grid.Column lWidth={3} sWidth={6} xsWidth={12} mWidth={3}>
                  <Input
                    label={SELECT_LABEL.serial}
                    indicator="required"
                    name="serialNumber"
                    placeholder={SELECT_PLACEHOLDER.serial}
                    value={
                      getUpdatedFormValues && getUpdatedFormValues.serialNumber
                        ? getUpdatedFormValues?.serialNumber
                        : ""
                    }
                    onChange={(data) => {
                      setValue("serialNumber", data, {
                        shouldValidate: true,
                      });
                    }}
                    error={
                      errors && errors.serialNumber
                        ? errors.serialNumber.message
                        : ""
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Card.Content>
        </Card>
        <Grid className="bottom-button-grid">
          <Grid.Row>
            <Grid.Column lWidth={6} xsWidth={12}>
              <Button
                type="secondary"
                size="medium"
                content="Back"
                onClick={() => {
                  dispatch(setCurrentWorkFlowStep(2));
                }}
              />
            </Grid.Column>
            <Grid.Column lWidth={6} xsWidth={12}>
              <Grid.Row className="aircraft-details-right-bottom-button">
                <Button
                  actionType="submit"
                  type="secondary"
                  size="medium"
                  content="Save as Draft"
                  disabled={saveDetailsLoader}
                  onClick={handleSubmit((data) =>
                    onClickSaveButton(data, "save")
                  )}
                />

                <Button
                  actionType="submit"
                  type="primary"
                  size="medium"
                  content="Next"
                  disabled={saveDetailsLoader}
                  onClick={handleSubmit((data) =>
                    onClickSaveButton(data, "next")
                  )}
                />
              </Grid.Row>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </form>
    </div>
  );
};
